import { useMemo, useState } from "react"
import { api, type Comment } from "../lib/api"
import { attachmentsNotInBody, resolveAttachmentHref } from "../lib/attachment-refs"
import { CommentEditor } from "./CommentEditor"
import { Markdown } from "./markdown"
import { AttachmentCard } from "./attachments"

type CommentDisplayProps = {
  comment: Comment
  onUpdate: () => void
  onDelete: () => void
  canEdit: boolean
  canDelete: boolean
}

export function CommentDisplay({
  comment,
  onUpdate,
  onDelete,
  canEdit,
  canDelete,
}: CommentDisplayProps) {
  const [editing, setEditing] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const attachments = comment.attachments ?? []

  // An image uploaded from inside the editor is stored as `attachment:<id>`,
  // because the signed URL that would otherwise be in the body expires in an
  // hour. This is what turns it back into a URL, freshly, on every render.
  const resolveHref = useMemo(() => resolveAttachmentHref(attachments), [attachments])

  // A file the body already shows inline does not also need a card under it.
  const cards = useMemo(
    () => attachmentsNotInBody(comment.body, attachments),
    [attachments, comment.body],
  )

  async function handleUpdate(
    body: string,
    removeAttachmentIds: string[],
    attachmentIds: string[]
  ) {
    const formData = new FormData()
    formData.append("body", body)
    if (removeAttachmentIds.length > 0) {
      formData.append("removeAttachmentIds", JSON.stringify(removeAttachmentIds))
    }
    if (attachmentIds.length > 0) {
      formData.append("attachmentIds", JSON.stringify(attachmentIds))
    }

    await api.patchMultipart(`/projects/comments/${comment.id}`, formData)
    setEditing(false)
    onUpdate()
  }

  async function handleDelete() {
    if (!confirm("Delete this comment?")) return
    setDeleting(true)
    try {
      await api.delete(`/projects/comments/${comment.id}`)
      onDelete()
    } finally {
      setDeleting(false)
    }
  }

  async function handleDeleteAttachment(attachmentId: string) {
    if (!confirm("Delete this attachment?")) return
    await api.delete(`/projects/attachments/${attachmentId}`)
    onUpdate()
  }

  if (editing) {
    return (
      <CommentEditor
        initialBody={comment.body}
        initialAttachments={comment.attachments || []}
        onSubmit={handleUpdate}
        onCancel={() => setEditing(false)}
        placeholder="Edit comment..."
        compact
      />
    )
  }

  return (
    <div className="group relative flex gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{comment.authorName}</span>
          <span className="text-xs text-muted-foreground">
            {new Date(comment.createdAt).toLocaleString()}
          </span>
          {comment.source === "agent" && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
              Agent
            </span>
          )}
        </div>

        <div className="mt-1">
          <Markdown resolveHref={resolveHref}>{comment.body}</Markdown>
        </div>

        {/* Attachments */}
        {cards.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {cards.map((att) => (
              <AttachmentCard
                key={att.id}
                attachment={att}
                canDelete={canDelete}
                onDelete={() => handleDeleteAttachment(att.id)}
              />
            ))}
          </div>
        )}

        {/* Actions */}
        {(canEdit || canDelete) && (
          <div className="mt-2 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
            {canEdit && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Edit
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="text-xs text-destructive hover:text-destructive/80 disabled:opacity-50"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
